import { apiRequest } from "./client";
import type {
  AuthUser,
  BrowserConfirmationRequired,
  ChangePasswordRequest,
  ClientLoginRequest,
  CompleteRegistrationRequest,
  ConfirmBrowserRequest,
  ConfirmBrowserResult,
  ForgotPasswordRequest,
  LoginResponse,
  LogoutRequest,
  ResetPasswordRequest,
} from "@kkp/api-client";
import { AuthTokens, LoginOutcome, LoginParams } from "../types/auth";

/**
 * Login API.
 *
 * Blueprint rule: UI should call src/api/* instead of fetch directly.
 *
 * A correct password on a browser not yet trusted for the PIC returns
 * `browserConfirmationRequired` with no session: the BFFE emailed a
 * confirmation link instead of authenticating this browser.
 */
export async function login(body: LoginParams): Promise<LoginOutcome> {
  const res = await apiRequest<
    LoginResponse | BrowserConfirmationRequired,
    ClientLoginRequest
  >({
    path: "/auth/login",
    method: "POST",
    body,
    auth: false,
  });

  if ("browserConfirmationRequired" in res) {
    return { status: "browserConfirmationRequired" };
  }

  return {
    status: "authenticated",
    tokens: { access: "cookie", refresh: "cookie" },
    user: res.user,
  };
}

/**
 * Confirm a browser binding from the emailed link. CSRF-exempt like login,
 * and never called automatically — only from the user's explicit click.
 */
export async function confirmBrowser(
  body: ConfirmBrowserRequest,
): Promise<ConfirmBrowserResult> {
  return apiRequest<ConfirmBrowserResult, ConfirmBrowserRequest>({
    path: "/auth/browser/confirm",
    method: "POST",
    body,
    auth: false,
  });
}

/**
 * Logout API.
 * - Expects both access & refresh tokens (single-key storage).
 * - POST to /auth/logout with refresh token.
 */
export async function logout(_tokens: AuthTokens): Promise<void> {
  await apiRequest<void, LogoutRequest>({
    path: "/auth/logout",
    method: "POST",
    body: {},
    auth: false,
  });
}

/**
 * Complete an invited user's registration.
 *
 * The server sets session cookies on success. The response body is ignored;
 * callers should follow up with `getProfile()` to hydrate the session.
 */
export async function completeRegistration(
  body: CompleteRegistrationRequest,
): Promise<void> {
  await apiRequest<unknown, CompleteRegistrationRequest>({
    path: "/auth/complete-registration",
    method: "POST",
    body,
    auth: false,
  });
}

/**
 * Request a password reset email.
 *
 * Server always returns 200 to avoid email enumeration.
 */
export async function forgotPassword(
  body: ForgotPasswordRequest,
): Promise<void> {
  await apiRequest<unknown, ForgotPasswordRequest>({
    path: "/auth/forgot-password",
    method: "POST",
    body,
    auth: false,
  });
}

/**
 * Reset a password using a single-use token.
 *
 * The server sets session cookies on success; callers should follow up with
 * `getProfile()` to hydrate the session.
 */
export async function resetPassword(body: ResetPasswordRequest): Promise<void> {
  await apiRequest<unknown, ResetPasswordRequest>({
    path: "/auth/reset-password",
    method: "POST",
    body,
    auth: false,
  });
}

export async function getProfile(): Promise<AuthUser> {
  return apiRequest<AuthUser>({
    path: "/auth/me",
    method: "GET",
    allowRefresh: false,
  });
}

/**
 * Change the current user's password.
 *
 * On success the server revokes every existing session for the user and
 * issues a fresh cookie pair for the calling device so the requester stays
 * signed in. Other devices are signed out.
 */
export async function changePassword(
  body: ChangePasswordRequest,
): Promise<void> {
  await apiRequest<unknown, ChangePasswordRequest>({
    path: "/auth/change-password",
    method: "POST",
    body,
    allowRefresh: false,
  });
}
